import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, Signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { VulcanIconComponent } from '@eagle-i/vulcan-component-library';
import * as echarts from 'echarts';
import moment from 'moment';
import { combineLatestWith, debounceTime, filter, fromEvent, of, ReplaySubject, startWith, switchMap, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { Territory } from '../../../../core/classes/territory';
import { AppService } from '../../../../services/app.service';
import { ReportsService } from '../../../../services/reports.service';
import { RestorationMetric } from '../../../../shared/classes/restoration-metric';
import { Report } from '../../classes/report';
import { ReportHeaderComponent } from '../report-header/report-header.component';
import { RestorationCurveTableComponent } from './restoration-curve-table/restoration-curve-table.component';

@Component({
  selector: 'tgrid-restoration-curve-report',
  imports: [
    MatCardModule,
    MatSelectModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatPaginatorModule,
    MatTooltip,
    ReportHeaderComponent,
    ReactiveFormsModule,
    MatInput,
    VulcanIconComponent,
    RestorationCurveTableComponent,
  ],
  templateUrl: './restoration-curve-report.component.html',
  styleUrls: ['../../reports.scss', './restoration-curve-report.component.scss'],
})
export class RestorationCurveReportComponent implements AfterViewInit {
  // Template Refs
  private chartElement = viewChild.required<ElementRef<HTMLDivElement>>('chart');
  private paginator = viewChild(MatPaginator);
  private sort = viewChild(MatSort);

  // Injected Services
  private activatedRoute = inject(ActivatedRoute);
  private reportService = inject(ReportsService);
  private appService = inject(AppService);

  protected report: Signal<Report> = toSignal(this.activatedRoute.data
    .pipe(
      takeUntilDestroyed(),
      map(({ report }) => report),
    ));


  // Table properties
  protected readonly columns: string[] = ['county', 'weather_event', 'start_time', 'end_time', 'peak_time', 'impact_level', 'auc'];
  public dataSource: MatTableDataSource<RestorationMetric> = new MatTableDataSource<RestorationMetric>([]);
  public asyncExpandedRow = new ReplaySubject<RestorationMetric>(1);

  // Filter Properties
  public filterGroup = new FormGroup({
    year: new FormControl<number>(moment().year() - 1, { nonNullable: true }),
    countyName: new FormControl<string>(''),
  });

  // Chart Properties
  private chart: echarts.ECharts;
  private readonly defaultOptions = {
    grid: {
      top: 35,
      bottom: 55,
      left: 45,
      right: 45,
    },
    tooltip: {
      trigger: 'axis',
    } as {trigger: string},
    xAxis: {
      boundaryGap: false,
      type: 'category',
      name: 'Percent Restored',
      nameLocation: 'middle',
      nameTextStyle: {
        padding: [10, 0, 0, 0],
      },
      axisLabel: {
        color: '#707070',
        formatter: (date: string) => {
          const value = parseInt(date);
          return value + '%';
        },
      },
    },
    yAxis: {
      type: 'value',
      name: 'Hours Since Peak Outage',
      nameLocation: 'middle',
      nameGap: 30,
    },
    series: [
      {
        name: 'customersOut',
        type: 'line',
        smooth: 0.6,
        areaStyle: {
          opacity: 0.66,
          color: '#FFF1CF',
        },
        lineStyle: {
          color: '#FFB800',
        }, itemStyle: {
          opacity: 0,
          color: '#FFB800',
        },
      },
    ],
  };

  constructor() {

    this.filterGroup.controls.year.valueChanges.pipe(
      startWith(this.filterGroup.controls.year.value),
      combineLatestWith(toObservable(this.appService.selectedStates)),
      takeUntilDestroyed(),
    ).subscribe(([, states]) => {
      this.getRestorationMetrics(states);
    });

    this.filterGroup.controls.countyName.valueChanges.pipe(
      debounceTime(500),
      takeUntilDestroyed(),
    ).subscribe(value => {
      this.dataSource.filter = value ? value.toLowerCase() : ' ';
    });


    fromEvent(window, 'resize').pipe(
      filter(() => !!this.chart),
      debounceTime(250),
      takeUntilDestroyed(),
    ).subscribe(() => this.chart.resize());


    this.asyncExpandedRow.pipe(
      filter(value => !!value),
      switchMap(metric => {
        if (metric.restorationCurves.length !== 0) return of(metric);
        return this.reportService.getRestorationCurveData(metric.outageId).pipe(
          tap(curves => metric.restorationCurves = curves),
          map(() => metric),
        );
      }),
    ).subscribe(curvePoints => {
      const xValues: number[] = [];
      const yValues: number[] = [];

      curvePoints.testRestorationTimes.forEach((value, key) => {
        xValues.push(key);
        yValues.push(value);
      });

      const opts = { ...this.defaultOptions } as echarts.EChartsOption;
      (opts.xAxis as {data: number[]}).data = xValues;
      (opts.series as Array<{data: number[]}>)[0].data = yValues;
      (opts.tooltip as {formatter: (param: {marker: string; data: number}[]) => string}).formatter = (param: {marker: string; data: number}[]) => {
        return `
            <p> ${param[0].marker} Measurment Date: ${curvePoints.peakTime.clone().add(param[0].data, 'hours').format('M/DD h:mm A')}</p>
            `;
      };

      this.chart.resize();
      this.chart.setOption(opts);
    });


  }

  ngAfterViewInit() {
    this.chart = echarts.init(this.chartElement().nativeElement);

    this.dataSource.paginator = this.paginator() as MatPaginator;
    this.dataSource.sortingDataAccessor = this.sortTableLogic;
    this.dataSource.filterPredicate = this.filterPredicate.bind(this);
    this.dataSource.sort = this.sort() as MatSort;
  }

  private sortTableLogic(row: RestorationMetric, headerName: string): string | number {
    switch (headerName) {
      case 'county':
        return row.countyName.toLowerCase();
      case 'weather_event':
        return row.phenomNames();
      case 'peak_time':
        return row.peakTime.valueOf();
      case 'start_time':
        return row.startDate.valueOf();
      case 'end_time':
        return row.endDate.valueOf();
      case 'event_duration':
        return row.eventDuration;
      case 'impact_duration':
        return row.impactDuration;
      case 'recovery_duration':
        return row.recoveryDuration;
      case 'impact_level':
        return row.impactLevel;
      case 'recovery_rate':
        return row.recoveryRate;
      case 'impact_rate':
        return row.impactRate;
      case 'impact_ratio':
        return row.recoveryImpactRatio;
      case 'auc':
        return row.auc;
      default:
        return '';
    }
  }

  private getRestorationMetrics(state: Territory[]): void {
    const year = this.filterGroup.controls.year.value;
    this.reportService.getRestorationMetrics([year], state)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (data) => {
          this.dataSource.data = data;
          this.asyncExpandedRow.next(this.dataSource._orderData(this.dataSource.filteredData)[0]);
        },
        error: (err) => {
          console.error('Failed to load restoration metrics:', err);
        },
      });
  }

  public toggleRow(row: RestorationMetric) {
    this.asyncExpandedRow.next(row);
  }

  private filterPredicate(row: RestorationMetric, searchValue: string): boolean {
    return row.countyName.toLowerCase().includes(searchValue.toLowerCase().trim());
  }

  /**
   * A utility function that is used to determine if an element is showing an ellipsis
   * @param element The element being checked for ellipsis
   * @protected
   */
  // TODO: Move this to a utility function file
  protected hasEllipsis(element: HTMLTableCellElement) {
    return (window as {elementHasEllipsis?: (el: HTMLTableCellElement) => boolean}).elementHasEllipsis?.(element);
  }
}
